// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    useGetLink,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@werner/App/Components/Common/SectionTitle";
import { getNotifyStyle } from "@werner/App/Components/Notify";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import InvoiceSummaryCard, { InvoiceSummaryCardStyles } from "@werner/App/Widgets/OpenInvoices/InvoiceSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    sectionTitleUrl = "sectionTitleUrl",
    sectionTitleText = "sectionTitleText",
    numberOfRecords = "numberOfRecords",
    showOnlyOpenInvoices = "showOnlyOpenInvoices",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.sectionTitleUrl]: LinkFieldValue;
        [fields.sectionTitleText]: string;
        [fields.numberOfRecords]: number;
        [fields.showOnlyOpenInvoices]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = xIsAuthenticated(state);
    const userRoles = isAuthenticated ? session.userRoles.trim().split(",") : [];

    return {
        settingsCollection: getSettingsCollection(state),
        session,
        invoicesDataView: getInvoicesDataView(state, openInvoicesParameter),
        widgetUrl: getPageLinkByNodeId(state, props.fields.sectionTitleUrl.value),
        canViewInvoices:
            isAuthenticated &&
            (userRoles.includes("Buyer1") || userRoles.includes("Finance") || userRoles.includes("Administrator")),
    };
};

const mapDispatchToProps = {
    loadInvoices,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenInvoicesStyles {
    cardList?: CardListStyles;
    noInvoicesGridItem?: GridItemProps;
    noInvoicesText?: TypographyPresentationProps;
    invoiceSummaryCard?: InvoiceSummaryCardStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    tableWrapper?: GridItemProps;
    wrapper?: InjectableCss;
}

export const recentQuotesStyles: OpenInvoicesStyles = {
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
    noInvoicesText: {
        ...getNotifyStyle("warning"),
    },
};

let openInvoicesParameter = {
    page: 1,
    pageSize: 5,
    customerSequence: "-1",
    showOpenOnly: false,
};

const styles = recentQuotesStyles;

class OpenInvoices extends React.Component<Props> {
    componentDidMount() {
        openInvoicesParameter.pageSize = this.props.fields.numberOfRecords;
        openInvoicesParameter.showOpenOnly = this.props.fields.showOnlyOpenInvoices;
        if (this.props.canViewInvoices && !this.props.invoicesDataView.value) {
            this.props.loadInvoices(openInvoicesParameter);
        }
    }

    render() {
        const { session, invoicesDataView, canViewInvoices, fields, widgetUrl } = this.props;
        const link = widgetUrl?.url;

        if (!canViewInvoices || !invoicesDataView.value || invoicesDataView.value.length === 0) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <SectionTitle
                    title={this.props.fields.title}
                    viewMoreLink={link}
                    viewMoreText={fields.sectionTitleText}
                    data-test-selector="cardListRecentInvoices"
                />
                {invoicesDataView.value.length === 0 && (
                    <GridItem width={12}>
                        <Typography {...styles.noInvoicesText}>{translate("No invoices found")}</Typography>
                    </GridItem>
                )}
                {invoicesDataView.value.length > 0 && (
                    <GridItem {...styles.tableWrapper} width={12}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Invoice Date")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Customer Rel #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Ship-To")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Due Date")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Total")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Balance")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("BillTrust")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {invoicesDataView.value.map(
                                    invoice =>
                                        invoice !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="openInvoices_dataLine"
                                                key={invoice.id}
                                            >
                                                <InvoiceSummaryCard
                                                    invoice={invoice}
                                                    session={session}
                                                    extendedStyles={styles.invoiceSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Invoices",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.sectionTitleUrl,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.sectionTitleText,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "View all",
                fieldType: "Translatable",
                sortOrder: 3,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Invoices Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOnlyOpenInvoices,
                displayName: "Show Only Open Invoices",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
